import { useState, useCallback, useMemo } from 'react';
import { Check, Calculator, FlaskConical, TrendingUp, Award } from 'lucide-react';
import { useExperiment } from '@/context';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

interface DataPoint {
  volume: number;
  pH: number;
}

interface DerivativePoint {
  volume: number;
  dpH_dV: number;
}

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Titration Curve',
    content: 'The S-shaped curve shows three regions: initial (low pH), buffer region (gradual rise), and equivalence point (steep rise).'
  },
  2: {
    title: 'First Derivative',
    content: 'The derivative dpH/dV shows the rate of pH change. The maximum occurs at the equivalence point where pH changes most rapidly.',
    chemicalEquation: 'dpH/dV = (pHi+1 - pHi)/(Vi+1 - Vi)'
  },
  4: {
    title: 'Finding the Endpoint',
    content: 'The volume at maximum dpH/dV is the equivalence point. This graphical method is more precise than visual indicators.'
  },
  5: {
    title: 'Stoichiometry',
    content: 'At the equivalence point: moles NaOH = moles aspirin. Since we know the molarity and volume of NaOH, we can calculate moles of aspirin.'
  },
  7: {
    title: 'Purity Calculation',
    content: 'Pharmaceutical-grade aspirin should be ≥99% pure. Tablet purity (including binders and fillers) is typically 85-95%.',
    chemicalEquation: '% Purity = (mass aspirin / mass tablet) × 100'
  },
  8: {
    title: 'Sources of Error',
    content: 'Common errors include: incomplete dissolution, overshooting the endpoint, air bubbles in the burette, or aspirin hydrolysis to salicylic acid.'
  }
};

export default function Part4Analysis() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();

  // State for info panel
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Get data from previous parts
  const sampleData = state?.experimentData?.samplePreparation?.sample || { tabletMass: 0.3256 };
  const titrationData = state?.experimentData?.titration?.titration || {
    dataPoints: [] as DataPoint[]
  };

  const partState = state?.experimentData?.analysis || {
    curve: {
      plotted: false,
      derivativePlotted: false
    },
    results: {
      equivalenceVolume: null,
      molesAspirin: null,
      massAspirin: null,
      purityPercent: null
    },
    firstDerivative: [] as DerivativePoint[]
  };

  const [currentStep, setCurrentStep] = useState(1);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('analysis', { ...partState, ...updates });
  }, [partState, updatePartData]);

  // Generate sample data if none exists
  const dataPoints: DataPoint[] = useMemo(() => {
    if (titrationData.dataPoints && titrationData.dataPoints.length > 5) {
      return titrationData.dataPoints;
    }
    // Generate sample titration curve data
    const points: DataPoint[] = [];
    for (let v = 0; v <= 35; v += 1) {
      let pH: number;
      if (v < 2) {
        pH = 3.0 + v * 0.15;
      } else if (v < 19) {
        pH = 3.3 + ((v - 2) / 17) * 2.5;
      } else if (v < 26) {
        const distFromEq = v - 22.5;
        pH = 8.5 + Math.atan(distFromEq * 2) * 2;
      } else {
        pH = Math.min(12.5, 10.5 + (v - 26) * 0.1);
      }
      points.push({ volume: v, pH: pH + (Math.random() - 0.5) * 0.1 });
    }
    return points;
  }, [titrationData.dataPoints]);

  // Calculate first derivative
  const calculateDerivative = useCallback(() => {
    const derivative: DerivativePoint[] = [];
    for (let i = 1; i < dataPoints.length; i++) {
      const dV = dataPoints[i].volume - dataPoints[i - 1].volume;
      const dpH = dataPoints[i].pH - dataPoints[i - 1].pH;
      const dpH_dV = dV > 0 ? dpH / dV : 0;
      const avgVolume = (dataPoints[i].volume + dataPoints[i - 1].volume) / 2;
      derivative.push({ volume: avgVolume, dpH_dV });
    }
    return derivative;
  }, [dataPoints]);

  const handlePlotCurve = useCallback(() => {
    updateState({ curve: { ...partState.curve, plotted: true } });
    completeStep('analysis-1');
    setCurrentStep(2);
  }, [partState.curve, updateState, completeStep]);

  const handleCalculateDerivative = useCallback(() => {
    const derivative = calculateDerivative();
    updateState({ firstDerivative: derivative });
    completeStep('analysis-2');
    setCurrentStep(3);
  }, [calculateDerivative, updateState, completeStep]);

  const handlePlotDerivative = useCallback(() => {
    updateState({ curve: { ...partState.curve, derivativePlotted: true } });
    completeStep('analysis-3');
    setCurrentStep(4);
  }, [partState.curve, updateState, completeStep]);

  const handleFindEquivalencePoint = useCallback(() => {
    // Find maximum in first derivative
    const derivative = partState.firstDerivative?.length > 0 ? partState.firstDerivative : calculateDerivative();
    let maxDpH = 0;
    let eqVolume = 22.5;

    derivative.forEach((point: DerivativePoint) => {
      if (point.dpH_dV > maxDpH) {
        maxDpH = point.dpH_dV;
        eqVolume = point.volume;
      }
    });

    updateState({
      results: { ...partState.results, equivalenceVolume: eqVolume }
    });
    completeStep('analysis-4');
    setCurrentStep(5);
  }, [partState.firstDerivative, partState.results, calculateDerivative, updateState, completeStep]);

  const handleCalculateMoles = useCallback(() => {
    if (partState.results.equivalenceVolume) {
      // moles NaOH = M * V(L)
      const molarity = 0.100; // M
      const volume = partState.results.equivalenceVolume / 1000; // Convert mL to L
      const moles = molarity * volume;

      updateState({
        results: { ...partState.results, molesAspirin: moles }
      });
      completeStep('analysis-5');
      setCurrentStep(6);
    }
  }, [partState.results, updateState, completeStep]);

  const handleCalculateMass = useCallback(() => {
    if (partState.results.molesAspirin) {
      // mass = moles * MW
      const molarMass = 180.16; // g/mol for aspirin
      const mass = partState.results.molesAspirin * molarMass;

      updateState({
        results: { ...partState.results, massAspirin: mass }
      });
      completeStep('analysis-6');
      setCurrentStep(7);
    }
  }, [partState.results, updateState, completeStep]);

  const handleCalculatePurity = useCallback(() => {
    if (partState.results.massAspirin && sampleData.tabletMass) {
      // % purity = (mass aspirin / tablet mass) * 100
      const purity = (partState.results.massAspirin / sampleData.tabletMass) * 100;

      updateState({
        results: { ...partState.results, purityPercent: purity }
      });
      completeStep('analysis-7');
      setCurrentStep(8);
    }
  }, [partState.results, sampleData.tabletMass, updateState, completeStep]);

  const handleEvaluateResults = useCallback(() => {
    completeStep('analysis-8');
  }, [completeStep]);

  // Get derivative for display
  const derivativeData = partState.firstDerivative?.length > 0 ? partState.firstDerivative : [];

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Calculator size={16} />
          <h3>Part IV: Data Analysis</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Plot Titration Curve', hint: 'pH vs Volume graph' },
            { id: 2, title: 'Calculate First Derivative', hint: 'dpH/dV for each interval' },
            { id: 3, title: 'Plot First Derivative', hint: 'Visualize endpoint peak' },
            { id: 4, title: 'Find Equivalence Point', hint: 'Volume at maximum dpH/dV' },
            { id: 5, title: 'Calculate Moles of Aspirin', hint: 'From moles NaOH' },
            { id: 6, title: 'Calculate Mass of Aspirin', hint: 'MW = 180.16 g/mol' },
            { id: 7, title: 'Calculate Purity', hint: '(mass aspirin / tablet mass) x 100' },
            { id: 8, title: 'Evaluate Results', hint: 'Compare to expected values' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`analysis-${step.id}`) ? 'completed' : ''} ${
                currentStep === step.id ? 'active' : ''
              }`}
            >
              <div className={`step-number ${isStepCompleted(`analysis-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`analysis-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          <h4>Results</h4>
          <div className="result-row">
            <span>Tablet Mass:</span>
            <span>{sampleData.tabletMass?.toFixed(4) || '-'} g</span>
          </div>
          <div className="result-row">
            <span>Eq. Volume:</span>
            <span>{partState.results.equivalenceVolume?.toFixed(2) || '-'} mL</span>
          </div>
          <div className="result-row">
            <span>Moles Aspirin:</span>
            <span>{partState.results.molesAspirin?.toExponential(4) || '-'}</span>
          </div>
          <div className="result-row">
            <span>Mass Aspirin:</span>
            <span>{partState.results.massAspirin?.toFixed(4) || '-'} g</span>
          </div>
          <div className="result-row highlight">
            <span>Purity:</span>
            <span>{partState.results.purityPercent?.toFixed(1) || '-'}%</span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Data Analysis"
        completedCount={state?.completedSteps.filter(s => s.startsWith('analysis-')).length || 0}
        totalSteps={8}
        equipment={[]}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="analysis-workspace">
          {/* Graphs */}
          <div className="graphs-container">
            {/* Titration Curve */}
            <div className={`graph-panel ${partState.curve.plotted ? 'visible' : ''}`}>
              <h4>Titration Curve (pH vs Volume)</h4>
              <div className="graph-container">
                <svg viewBox="0 0 300 180" className="analysis-graph">
                  {/* Grid */}
                  {[0, 2, 4, 6, 8, 10, 12, 14].map((pH) => (
                    <line
                      key={`h-${pH}`}
                      x1="40"
                      y1={160 - pH * 10}
                      x2="290"
                      y2={160 - pH * 10}
                      stroke="var(--border)"
                      strokeWidth="0.5"
                      strokeDasharray="2,2"
                    />
                  ))}

                  {/* Axes */}
                  <line x1="40" y1="160" x2="290" y2="160" stroke="var(--text-secondary)" strokeWidth="2" />
                  <line x1="40" y1="20" x2="40" y2="160" stroke="var(--text-secondary)" strokeWidth="2" />

                  {/* Labels */}
                  <text x="165" y="178" fontSize="10" fill="var(--text-secondary)" textAnchor="middle">Volume NaOH (mL)</text>
                  <text x="15" y="90" fontSize="10" fill="var(--text-secondary)" textAnchor="middle" transform="rotate(-90, 15, 90)">pH</text>

                  {/* Data curve */}
                  {partState.curve.plotted && (
                    <polyline
                      points={dataPoints.map(dp =>
                        `${40 + dp.volume * 7},${160 - dp.pH * 10}`
                      ).join(' ')}
                      fill="none"
                      stroke="var(--primary)"
                      strokeWidth="2"
                    />
                  )}

                  {/* Equivalence point marker */}
                  {partState.results.equivalenceVolume && (
                    <g>
                      <line
                        x1={40 + partState.results.equivalenceVolume * 7}
                        y1="20"
                        x2={40 + partState.results.equivalenceVolume * 7}
                        y2="160"
                        stroke="var(--secondary)"
                        strokeWidth="2"
                        strokeDasharray="4,4"
                      />
                      <circle
                        cx={40 + partState.results.equivalenceVolume * 7}
                        cy={160 - 8.5 * 10}
                        r="6"
                        fill="var(--secondary)"
                      />
                      <text
                        x={45 + partState.results.equivalenceVolume * 7}
                        y="40"
                        fontSize="9"
                        fill="var(--secondary)"
                      >
                        Eq. Point
                      </text>
                    </g>
                  )}
                </svg>
              </div>
            </div>

            {/* First Derivative */}
            <div className={`graph-panel ${partState.curve.derivativePlotted ? 'visible' : ''}`}>
              <h4>First Derivative (dpH/dV)</h4>
              <div className="graph-container">
                <svg viewBox="0 0 300 180" className="analysis-graph">
                  {/* Axes */}
                  <line x1="40" y1="160" x2="290" y2="160" stroke="var(--text-secondary)" strokeWidth="2" />
                  <line x1="40" y1="20" x2="40" y2="160" stroke="var(--text-secondary)" strokeWidth="2" />

                  {/* Labels */}
                  <text x="165" y="178" fontSize="10" fill="var(--text-secondary)" textAnchor="middle">Volume NaOH (mL)</text>
                  <text x="15" y="90" fontSize="10" fill="var(--text-secondary)" textAnchor="middle" transform="rotate(-90, 15, 90)">dpH/dV</text>

                  {/* Derivative curve */}
                  {partState.curve.derivativePlotted && derivativeData.length > 0 && (
                    <polyline
                      points={derivativeData.map((dp: DerivativePoint) => {
                        const yValue = Math.min(dp.dpH_dV * 30, 140);
                        return `${40 + dp.volume * 7},${160 - yValue}`;
                      }).join(' ')}
                      fill="none"
                      stroke="var(--accent)"
                      strokeWidth="2"
                    />
                  )}

                  {/* Peak marker */}
                  {partState.results.equivalenceVolume && derivativeData.length > 0 && (
                    <g>
                      <circle
                        cx={40 + partState.results.equivalenceVolume * 7}
                        cy="30"
                        r="6"
                        fill="var(--secondary)"
                      />
                      <text
                        x={45 + partState.results.equivalenceVolume * 7}
                        y="50"
                        fontSize="9"
                        fill="var(--secondary)"
                      >
                        Max dpH/dV
                      </text>
                    </g>
                  )}
                </svg>
              </div>
            </div>
          </div>

          {/* Calculation Steps */}
          <div className="calculation-steps">
            {currentStep === 1 && (
              <button className="calc-btn" onClick={handlePlotCurve}>
                <TrendingUp size={18} />
                Plot Titration Curve
              </button>
            )}

            {currentStep === 2 && (
              <div className="calc-card">
                <h4>Calculate First Derivative</h4>
                <div className="formula">dpH/dV = (pH<sub>i+1</sub> - pH<sub>i</sub>) / (V<sub>i+1</sub> - V<sub>i</sub>)</div>
                <button className="calc-btn" onClick={handleCalculateDerivative}>
                  <Calculator size={18} />
                  Calculate Derivative
                </button>
              </div>
            )}

            {currentStep === 3 && (
              <button className="calc-btn" onClick={handlePlotDerivative}>
                <TrendingUp size={18} />
                Plot First Derivative
              </button>
            )}

            {currentStep === 4 && (
              <div className="calc-card">
                <h4>Find Equivalence Point</h4>
                <p>The equivalence point is where dpH/dV is maximum.</p>
                <button className="calc-btn" onClick={handleFindEquivalencePoint}>
                  <Check size={18} />
                  Find Maximum
                </button>
              </div>
            )}

            {currentStep === 5 && (
              <div className="calc-card">
                <h4>Calculate Moles of Aspirin</h4>
                <div className="formula">
                  moles = M<sub>NaOH</sub> × V<sub>eq</sub>
                  <br />
                  = 0.100 M × {(partState.results.equivalenceVolume || 0) / 1000} L
                </div>
                <button className="calc-btn" onClick={handleCalculateMoles}>
                  <Calculator size={18} />
                  Calculate Moles
                </button>
              </div>
            )}

            {currentStep === 6 && (
              <div className="calc-card">
                <h4>Calculate Mass of Aspirin</h4>
                <div className="formula">
                  mass = moles × MW
                  <br />
                  = {partState.results.molesAspirin?.toExponential(4)} × 180.16 g/mol
                </div>
                <button className="calc-btn" onClick={handleCalculateMass}>
                  <Calculator size={18} />
                  Calculate Mass
                </button>
              </div>
            )}

            {currentStep === 7 && (
              <div className="calc-card">
                <h4>Calculate Purity</h4>
                <div className="formula">
                  % Purity = (mass<sub>aspirin</sub> / mass<sub>tablet</sub>) × 100
                  <br />
                  = ({partState.results.massAspirin?.toFixed(4)} / {sampleData.tabletMass?.toFixed(4)}) × 100
                </div>
                <button className="calc-btn" onClick={handleCalculatePurity}>
                  <Calculator size={18} />
                  Calculate Purity
                </button>
              </div>
            )}

            {currentStep === 8 && !isStepCompleted('analysis-8') && (
              <div className="calc-card">
                <h4>Evaluate Results</h4>
                <div className="purity-result">
                  <Award size={48} color={partState.results.purityPercent && partState.results.purityPercent > 90 ? '#22C55E' : '#EF4444'} />
                  <div>
                    <h3>{partState.results.purityPercent?.toFixed(1)}% Pure</h3>
                    <p>
                      {partState.results.purityPercent && partState.results.purityPercent > 90
                        ? 'Aspirin meets typical purity standards (>90%)'
                        : 'Purity is below expected levels. Check for degradation.'}
                    </p>
                  </div>
                </div>
                <button className="calc-btn success" onClick={handleEvaluateResults}>
                  <Check size={18} />
                  Complete Analysis
                </button>
              </div>
            )}

            {isStepCompleted('analysis-8') && (
              <div className="completion-banner">
                <FlaskConical size={32} />
                <div>
                  <h3>Experiment Complete!</h3>
                  <p>
                    You have successfully determined the purity of the aspirin sample to be{' '}
                    <strong>{partState.results.purityPercent?.toFixed(1)}%</strong> using potentiometric titration.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </LabBench>

      <style>{`
        .analysis-workspace {
          display: flex;
          flex-direction: column;
          gap: 24px;
          padding: 20px;
          width: 100%;
        }

        .graphs-container {
          display: flex;
          gap: 20px;
        }

        .graph-panel {
          flex: 1;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          opacity: 0.5;
          transition: opacity 0.3s;
        }

        .graph-panel.visible {
          opacity: 1;
        }

        .graph-panel h4 {
          font-size: 13px;
          margin: 0 0 12px 0;
          color: var(--text-primary);
          text-align: center;
        }

        .graph-container {
          background: var(--bg-dark);
          border-radius: 8px;
          padding: 8px;
        }

        .analysis-graph {
          width: 100%;
          height: auto;
        }

        .calculation-steps {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .calc-card {
          background: var(--bg-surface);
          border: 2px solid var(--primary);
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          max-width: 400px;
        }

        .calc-card h4 {
          font-size: 16px;
          margin: 0 0 12px 0;
        }

        .calc-card p {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0 0 16px 0;
        }

        .formula {
          background: var(--bg-dark);
          padding: 12px;
          border-radius: 6px;
          font-family: 'Times New Roman', serif;
          font-size: 14px;
          margin-bottom: 16px;
        }

        .calc-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 24px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .calc-btn:hover {
          transform: translateY(-2px);
        }

        .calc-btn.success {
          background: var(--secondary);
        }

        .purity-result {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: var(--bg-elevated);
          border-radius: 8px;
          margin-bottom: 16px;
          text-align: left;
        }

        .purity-result h3 {
          margin: 0 0 4px 0;
          font-size: 20px;
        }

        .purity-result p {
          margin: 0;
          font-size: 12px;
        }

        .completion-banner {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          border-radius: 12px;
          color: white;
          max-width: 500px;
        }

        .completion-banner h3 {
          margin: 0 0 4px 0;
          font-size: 18px;
        }

        .completion-banner p {
          margin: 0;
          font-size: 14px;
          opacity: 0.9;
        }

        .results-summary {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .results-summary h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 12px 0;
          text-transform: uppercase;
        }

        .result-row {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          padding: 4px 0;
          border-bottom: 1px solid var(--border);
        }

        .result-row:last-child {
          border-bottom: none;
        }

        .result-row span:first-child {
          color: var(--text-secondary);
        }

        .result-row span:last-child {
          color: var(--text-primary);
          font-family: 'Courier New', monospace;
        }

        .result-row.highlight {
          background: rgba(0, 168, 107, 0.1);
          padding: 6px;
          border-radius: 4px;
          margin-top: 8px;
        }

        .result-row.highlight span:last-child {
          color: var(--secondary);
          font-weight: 700;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
